import { useEffect, useRef, useState } from "react";
import { Loader2, Lock, Maximize, Minimize, Pause, Play } from "lucide-react";

const YT_API = "https://www.youtube.com/iframe_api";

export function youtubeId(url) {
  const m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/.exec(url || "");
  return m ? m[1] : null;
}

function loadYT() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!window._ytReady) {
    window._ytReady = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      const s = document.createElement("script");
      s.src = YT_API;
      document.head.appendChild(s);
    });
  }
  return window._ytReady;
}

const fmt = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

function useVideoFullscreen() {
  const wrapRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFs(document.fullscreenElement === wrapRef.current || document.webkitFullscreenElement === wrapRef.current);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => { document.removeEventListener("fullscreenchange", onChange); document.removeEventListener("webkitfullscreenchange", onChange); };
  }, []);
  const toggleFullscreen = () => {
    const el = wrapRef.current;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      (el?.requestFullscreen || el?.webkitRequestFullscreen)?.call(el);
    }
  };
  return { wrapRef, isFs, toggleFullscreen };
}

function Controls({ playing, onToggle, current, duration, isFs, onFullscreen }) {
  return (
    <div className="mt-3 flex items-center gap-3" data-testid="locked-video-controls">
      <button onClick={onToggle} data-testid="locked-video-toggle" className="w-11 h-11 shrink-0 rounded-full bg-blue-600 text-white grid place-items-center active:scale-95 transition hover:bg-blue-700">
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>
      <div className="flex-1">
        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden cursor-not-allowed" title="Video tidak dapat dipercepat">
          <div className="h-full bg-blue-600" style={{ width: `${duration ? Math.min(100, (current / duration) * 100) : 0}%`, transition: "width .3s linear" }} />
        </div>
        <p className="mt-1.5 text-[11px] font-bold text-slate-400 tabular-nums flex items-center gap-1.5">
          <Lock className="w-3 h-3" />{fmt(current)} / {fmt(duration)} · tidak bisa dipercepat
        </p>
      </div>
      <button onClick={onFullscreen} data-testid="locked-video-fullscreen" className="w-11 h-11 shrink-0 rounded-full bg-slate-100 text-slate-600 grid place-items-center active:scale-95 transition hover:bg-slate-200">
        {isFs ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
      </button>
    </div>
  );
}

function YouTubeLocked({ url, onProgress }) {
  const holder = useRef(null);
  const player = useRef(null);
  const maxRef = useRef(0);
  const reportRef = useRef(onProgress);
  reportRef.current = onProgress;
  const { wrapRef, isFs, toggleFullscreen } = useVideoFullscreen();
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [state, setState] = useState({ current: 0, duration: 0 });

  useEffect(() => {
    let timer, destroyed = false;
    maxRef.current = 0;
    loadYT().then((YT) => {
      if (destroyed || !holder.current) return;
      player.current = new YT.Player(holder.current, {
        videoId: youtubeId(url),
        playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0, fs: 0, playsinline: 1, iv_load_policy: 3 },
        events: {
          onReady: (e) => { e.target.setPlaybackRate(1); setReady(true); setState({ current: 0, duration: e.target.getDuration() }); },
          onStateChange: (e) => {
            setPlaying(e.data === 1);
            if (e.data === 0) reportRef.current?.({ watched: e.target.getDuration() || maxRef.current, duration: e.target.getDuration() || 0, ended: true });
          },
          onPlaybackRateChange: (e) => { if (e.target.getPlaybackRate() !== 1) e.target.setPlaybackRate(1); },
        },
      });
      timer = setInterval(() => {
        const p = player.current;
        if (!p?.getCurrentTime) return;
        if (p.getPlaybackRate && p.getPlaybackRate() !== 1) p.setPlaybackRate(1);
        const t = p.getCurrentTime() || 0;
        if (t > maxRef.current + 0.75) p.seekTo(maxRef.current, true);
        else maxRef.current = Math.max(maxRef.current, t);
        const duration = p.getDuration() || 0;
        setState({ current: Math.min(t, maxRef.current + 0.5), duration });
        reportRef.current?.({ watched: maxRef.current, duration, ended: false });
      }, 250);
    });
    return () => { destroyed = true; clearInterval(timer); player.current?.destroy?.(); };
  }, [url]);

  const toggle = () => {
    const p = player.current;
    if (!p) return;
    if (playing) p.pauseVideo();
    else p.playVideo();
  };

  return (
    <div ref={wrapRef} data-testid="locked-video-youtube" className={isFs ? "bg-slate-900 w-screen h-screen flex flex-col items-center justify-center p-4" : ""}>
      <div className={`relative rounded-2xl overflow-hidden bg-slate-900 ${isFs ? "w-full max-w-5xl aspect-video" : "aspect-video"}`}>
        <div ref={holder} className="w-full h-full" />
        <div className="absolute inset-0" onClick={toggle} onContextMenu={(e) => e.preventDefault()} data-testid="locked-video-overlay" style={{ cursor: "pointer" }} />
        {!ready && <span className="absolute inset-0 grid place-items-center text-white/70"><Loader2 className="w-6 h-6 animate-spin" /></span>}
      </div>
      <div className={isFs ? "w-full max-w-5xl" : ""}>
        <Controls playing={playing} onToggle={toggle} current={state.current} duration={state.duration} isFs={isFs} onFullscreen={toggleFullscreen} />
      </div>
    </div>
  );
}

function FileLocked({ url, onProgress }) {
  const ref = useRef(null);
  const maxRef = useRef(0);
  const reportRef = useRef(onProgress);
  reportRef.current = onProgress;
  const { wrapRef, isFs, toggleFullscreen } = useVideoFullscreen();
  const [playing, setPlaying] = useState(false);
  const [state, setState] = useState({ current: 0, duration: 0 });

  const onTime = () => {
    const v = ref.current;
    if (!v) return;
    if (v.playbackRate !== 1) v.playbackRate = 1;
    if (v.currentTime > maxRef.current + 1) v.currentTime = maxRef.current;
    else maxRef.current = Math.max(maxRef.current, v.currentTime);
    setState({ current: v.currentTime, duration: v.duration || 0 });
    reportRef.current?.({ watched: maxRef.current, duration: v.duration || 0, ended: false });
  };
  const lockRate = () => { const v = ref.current; if (v && v.playbackRate !== 1) v.playbackRate = 1; };
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (playing) v.pause();
    else v.play();
  };

  return (
    <div ref={wrapRef} data-testid="locked-video-file" className={isFs ? "bg-slate-900 w-screen h-screen flex flex-col items-center justify-center p-4" : ""}>
      <video ref={ref} src={url} className={`w-full rounded-2xl bg-slate-900 ${isFs ? "max-w-5xl aspect-video" : "aspect-video"}`} playsInline controls={false}
        controlsList="nodownload noplaybackrate" disablePictureInPicture
        onClick={toggle} onTimeUpdate={onTime} onSeeking={onTime} onRateChange={lockRate} onPlay={() => { lockRate(); setPlaying(true); }} onPause={() => setPlaying(false)}
        onEnded={() => { const v = ref.current; reportRef.current?.({ watched: v?.duration || maxRef.current, duration: v?.duration || 0, ended: true }); setPlaying(false); }}
        onContextMenu={(e) => e.preventDefault()} onLoadedMetadata={onTime} data-testid="locked-video-element" />
      <div className={isFs ? "w-full max-w-5xl" : ""}>
        <Controls playing={playing} onToggle={toggle} current={state.current} duration={state.duration} isFs={isFs} onFullscreen={toggleFullscreen} />
      </div>
    </div>
  );
}

export default function LockedVideo({ url, onProgress }) {
  const id = youtubeId(url);
  if (id) return <YouTubeLocked url={url} onProgress={onProgress} />;
  return <FileLocked url={url} onProgress={onProgress} />;
}
